import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHatWizard } from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import { getCanonicalUrl } from '../../../utils/seo';

import playgroundVideo from '../../../assets/BelvoirCastlePlayground.mp4';
import safari1 from '../../../assets/safari/1.png';
import safari2 from '../../../assets/safari/2.png';
import safari3 from '../../../assets/safari/3.png';
import safari4 from '../../../assets/safari/4.png';
import safari5 from '../../../assets/safari/5.png';
import safari6 from '../../../assets/safari/6.png';
import safariAnimals from '../../../assets/safari/play animals.png';

const PEACOCK = 'https://www.belvoircastle.com/wp/wp-content/uploads/2026/04/white-peacock.png';
const HERO_COVER = 'https://www.belvoircastle.com/wp/wp-content/uploads/2026/06/7.png';
const IMG_CASTLE_GARDENS = 'https://www.belvoircastle.com/wp/wp-content/uploads/2026/07/brightened8.png';
const IMG_CASTLE_WONDER = 'https://www.belvoircastle.com/wp/wp-content/uploads/2026/07/brightened9.png';
const IMG_TRAIL_BOOKLET = 'https://www.belvoircastle.com/wp/wp-content/uploads/2026/07/Screenshot-2026-07-20-at-16.03.30-scaled.png';
const IMG_RETAIL_DINING = 'https://www.belvoircastle.com/wp/wp-content/uploads/2026/07/brightened5.png';

const S = {
  jungle: '#0C2C1B',
  forest: '#1B5E3B',
  safari: '#C8861A',
  terra: '#A83E18',
  sage: '#567A5C',
  cream: '#FBF4E8',
  parchment: '#F4EDD8',
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ──────────────────────────────────────
   PAGE SHELL
   ────────────────────────────────────── */
const Page = styled.div`
  min-height: 100vh;
  background: ${S.cream};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

/* ──────────────────────────────────────
   HERO
   ────────────────────────────────────── */
const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(520px, 78vh, 820px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;

  @media (max-width: 768px) {
    height: auto;
    min-height: clamp(560px, 88svh, 760px);
    align-items: flex-end;
  }
`;

const HeroMedia = styled.div`
  position: absolute;
  inset: 0;
`;

const HeroCover = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;

  @media (max-width: 768px) {
    object-position: center 25%;
  }
`;

const HeroVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  opacity: ${p => (p.$visible ? 1 : 0)};
  transition: opacity 1.2s ease-in-out;

  @media (max-width: 768px) {
    object-position: center 25%;
  }
`;

const HeroImageThenVideo = ({ image, videoSrc, delay = 1000 }) => {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [allowVideo, setAllowVideo] = useState(false);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setAllowVideo(!reduceMotion.matches);
    update();
    reduceMotion.addEventListener('change', update);
    return () => reduceMotion.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!allowVideo) {
      setStarted(false);
      setPlaying(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setStarted(true), delay);
    return () => window.clearTimeout(timer);
  }, [allowVideo, delay]);

  useEffect(() => {
    if (!started || !allowVideo) return;
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', 'true');

    const tryPlay = () => {
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    };

    tryPlay();
    video.addEventListener('canplay', tryPlay);

    const onGesture = () => {
      tryPlay();
      window.removeEventListener('touchstart', onGesture);
      window.removeEventListener('click', onGesture);
    };
    window.addEventListener('touchstart', onGesture, { passive: true });
    window.addEventListener('click', onGesture);

    return () => {
      video.removeEventListener('canplay', tryPlay);
      window.removeEventListener('touchstart', onGesture);
      window.removeEventListener('click', onGesture);
    };
  }, [started, allowVideo, videoSrc]);

  return (
    <HeroMedia aria-hidden>
      <HeroCover src={image} alt="" />
      {started && allowVideo && (
        <HeroVideo
          ref={videoRef}
          src={videoSrc}
          $visible={playing}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onPlaying={() => setPlaying(true)}
        />
      )}
    </HeroMedia>
  );
};

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to top,
      rgba(12,44,27,0.92) 0%,
      rgba(12,44,27,0.65) 30%,
      rgba(12,44,27,0.15) 62%,
      transparent 100%
    );

  @media (max-width: 768px) {
    background:
      linear-gradient(to top,
        rgba(12,44,27,0.97) 0%,
        rgba(12,44,27,0.88) 38%,
        rgba(12,44,27,0.55) 62%,
        rgba(12,44,27,0.2) 100%
      );
  }
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.18;
  background-image:
    repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(200,134,26,0.35) 38px, rgba(200,134,26,0.35) 40px),
    repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(27,94,59,0.5) 38px, rgba(27,94,59,0.5) 40px);
  pointer-events: none;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
  animation: ${fadeUp} 0.7s ease both;

  @media (max-width: 768px) {
    padding: 1.35rem 1.1rem 1.75rem;
  }
`;

const HeroKicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${S.safari};

  @media (max-width: 480px) {
    font-size: 0.62rem;
    letter-spacing: 0.14em;
    line-height: 1.45;
  }
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.75rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2.75rem, 7vw, 5.5rem);
  font-weight: 700;
  line-height: 1.0;
  color: #fff;

  em {
    font-style: italic;
    color: ${S.safari};
  }

  @media (max-width: 768px) {
    font-size: clamp(2.15rem, 10vw, 2.75rem);
    margin-bottom: 0.55rem;
  }
`;

const HeroStrapline = styled.p`
  margin: 0 0 1.75rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.1rem, 2.4vw, 1.5rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.82);
  max-width: 36rem;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
    font-size: 1rem;
    line-height: 1.45;
  }
`;

const HeroBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 0.75rem;
  margin-bottom: 1.75rem;

  @media (max-width: 768px) {
    gap: 0.45rem;
    margin-bottom: 1rem;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => p.$fill ? S.safari : 'rgba(255,255,255,0.12)'};
  color: #fff;
  border: 1px solid ${p => p.$fill ? 'transparent' : 'rgba(255,255,255,0.28)'};
  backdrop-filter: blur(8px);

  ${p => p.$hideMobile && `
    @media (max-width: 768px) {
      display: none;
    }
  `}

  @media (max-width: 768px) {
    font-size: 0.62rem;
    letter-spacing: 0.05em;
    padding: 0.4rem 0.75rem;
    line-height: 1.35;
    text-align: center;
    white-space: normal;
    max-width: 100%;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.55rem;
    width: 100%;

    button {
      width: 100%;
      padding: 0.85rem 1.25rem;
      font-size: 0.75rem;
    }
  }
`;

const BtnPrimary = styled.button`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: ${S.safari};
  color: #fff;
  box-shadow: 0 6px 24px rgba(200,134,26,0.42);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(200,134,26,0.52);
  }
`;

const BtnGhost = styled.button`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.72);
  cursor: pointer;
  background: rgba(255,255,255,0.08);
  color: #fff;
  backdrop-filter: blur(8px);
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255,255,255,0.18);
  }

  ${p => p.$hideMobile && `
    @media (max-width: 768px) {
      display: none;
    }
  `}
`;

const ShareFeedback = styled.p`
  width: 100%;
  margin: 0.65rem 0 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  color: ${S.safari};
  letter-spacing: 0.04em;
  min-height: 1.1rem;
`;

/* ──────────────────────────────────────
   GROUP SAVER BANNER
   ────────────────────────────────────── */
const OfferBanner = styled.div`
  background: linear-gradient(135deg, ${S.terra} 0%, #8a2e10 100%);
  padding: clamp(1rem, 2.5vw, 1.4rem) clamp(1rem, 4vw, 2rem);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  text-align: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding: 1.1rem 1rem 1.25rem;

    button {
      width: 100%;
    }
  }
`;

const OfferText = styled.p`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.05rem, 2.5vw, 1.35rem);
  font-weight: 600;
  color: #fff;
  line-height: 1.35;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.45;
  }
`;

const OfferTag = styled.span`
  display: inline-block;
  background: ${S.safari};
  color: #fff;
  padding: 0.3rem 0.8rem;
  border-radius: 4px;
  font-family: system-ui, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: 768px) {
    align-self: center;
  }
`;

/* ──────────────────────────────────────
   SECTIONS
   ────────────────────────────────────── */
const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => p.$border ? `1px solid rgba(28,28,30,0.07)` : 'none'};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const SectionKicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${p => p.$color || S.safari};
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.85rem, 4.5vw, 3rem);
  font-weight: 700;
  line-height: 1.08;
  color: ${p => p.$light ? '#fff' : S.jungle};

  em {
    font-style: italic;
    color: ${S.safari};
  }
`;

const SectionIntro = styled.p`
  max-width: 640px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.65;
  color: ${p => p.$light ? 'rgba(255,255,255,0.72)' : 'rgba(28,28,30,0.62)'};
  margin: 0 0 clamp(2rem, 4vw, 3rem);
`;

/* ──────────────────────────────────────
   WHAT'S INCLUDED
   ────────────────────────────────────── */
const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: clamp(1rem, 2vw, 1.5rem);
`;

const FeatureCard = styled.div`
  background: ${p => p.$dark ? S.jungle : '#fff'};
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid ${p => p.$dark ? 'transparent' : 'rgba(28,28,30,0.08)'};
  box-shadow: 0 4px 24px rgba(16,29,64,0.07);
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(200, 134, 26, 0.1);
  border-radius: 10px;
  margin-bottom: 1rem;
  flex-shrink: 0;
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: ${p => p.$light ? '#fff' : S.jungle};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.6;
  color: ${p => p.$light ? 'rgba(255,255,255,0.68)' : 'rgba(28,28,30,0.6)'};
`;

/* ──────────────────────────────────────
   TRAIL STEPS
   ────────────────────────────────────── */
const TrailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: clamp(2.5rem, 5vw, 3.5rem);

  @media (max-width: 860px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TrailStep = styled.div`
  background: ${p => p.$bg};
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
  color: #fff;
`;

const TrailNum = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 2.25rem;
  font-weight: 700;
  color: rgba(255,255,255,0.25);
  line-height: 1;
  margin-bottom: 0.75rem;
`;

const TrailStepTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
`;

const TrailStepBody = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem;
  line-height: 1.6;
  color: rgba(255,255,255,0.72);
`;

/* ──────────────────────────────────────
   IMAGE GALLERY
   ────────────────────────────────────── */
const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 260px 260px;
  gap: clamp(0.5rem, 1vw, 0.75rem);
  border-radius: 14px;
  overflow: hidden;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 180px 180px 180px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: repeat(4, 160px);
  }
`;

const GalleryItem = styled.div`
  overflow: hidden;
  ${p => p.$span2 ? 'grid-row: span 2;' : ''}

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  &:hover img {
    transform: scale(1.04);
  }

  @media (max-width: 768px) {
    ${p => p.$span2 ? 'grid-row: span 1;' : ''}
  }
`;

/* ──────────────────────────────────────
   GROUP SAVER
   ────────────────────────────────────── */
const SaverGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(200,134,26,0.2);

  @media (max-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const SaverTier = styled.div`
  background: ${p => p.$active ? S.safari : p.$dark ? S.jungle : S.parchment};
  padding: clamp(1rem, 2.5vw, 1.5rem) 1rem;
  text-align: center;
`;

const SaverTickets = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.85rem;
  font-weight: 700;
  color: ${p => p.$light ? '#fff' : S.jungle};
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const SaverDiscount = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${p => p.$light ? 'rgba(255,255,255,0.8)' : S.forest};
  margin-bottom: 0.5rem;
`;

const SaverLabel = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.65rem;
  color: ${p => p.$light ? 'rgba(255,255,255,0.55)' : 'rgba(28,28,30,0.5)'};
  letter-spacing: 0.06em;
`;

/* ──────────────────────────────────────
   PULLQUOTE
   ────────────────────────────────────── */
const PullQuote = styled.blockquote`
  margin: 0;
  padding: clamp(1.5rem, 3vw, 2.25rem) clamp(1.5rem, 4vw, 2.5rem);
  background: ${S.jungle};
  border-left: 4px solid ${S.safari};
  border-radius: 0 12px 12px 0;

  p {
    margin: 0 0 0.65rem;
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: clamp(1.25rem, 2.8vw, 1.65rem);
    font-style: italic;
    font-weight: 300;
    color: #fff;
    line-height: 1.45;
  }

  cite {
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${S.safari};
    font-style: normal;
  }
`;

/* ──────────────────────────────────────
   ESSENTIAL INFO
   ────────────────────────────────────── */
const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1px;
  background: rgba(28,28,30,0.08);
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
`;

const InfoLabel = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(28,28,30,0.4);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: ${S.jungle};
  line-height: 1.25;
`;

/* ──────────────────────────────────────
   CTA SECTION
   ────────────────────────────────────── */
const CtaSection = styled.section`
  background: ${S.jungle};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(200,134,26,0.04) 38px, rgba(200,134,26,0.04) 40px),
      repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(27,94,59,0.07) 38px, rgba(27,94,59,0.07) 40px);
    pointer-events: none;
  }
`;

const CtaInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;

  em {
    font-style: italic;
    color: ${S.safari};
  }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255,255,255,0.68);
  line-height: 1.6;
`;

const CtaBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  align-items: center;
`;

const CtaShareBtn = styled.button`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1.05rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  cursor: pointer;
  background: transparent;
  color: #fff;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.85);
  }
`;

const CtaBtn = styled.button`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1.15rem 2.75rem;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: ${S.safari};
  color: #fff;
  box-shadow: 0 8px 32px rgba(200,134,26,0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(200,134,26,0.52);
  }
`;

const CtaMeta = styled.p`
  margin: 1.25rem 0 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.38);
  letter-spacing: 0.06em;
`;

const MidCta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.5rem;
  margin-top: clamp(2rem, 4vw, 2.75rem);
  padding: clamp(1.25rem, 3vw, 1.75rem) clamp(1.5rem, 4vw, 2rem);
  border-radius: 12px;
  background: ${p => (p.$dark ? S.jungle : S.parchment)};
  border: 1px solid rgba(200, 134, 26, 0.22);
`;

const MidCtaText = styled.p`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.05rem, 2.2vw, 1.3rem);
  font-weight: 600;
  line-height: 1.35;
  color: ${p => (p.$light ? '#fff' : S.jungle)};
  flex: 1;
  min-width: min(100%, 16rem);
`;

const MidCtaBtn = styled.button`
  flex-shrink: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.9rem 1.75rem;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: ${S.safari};
  color: #fff;
  box-shadow: 0 4px 18px rgba(200, 134, 26, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 22px rgba(200, 134, 26, 0.45);
  }
`;

/* ──────────────────────────────────────
   TWO-COL LAYOUT
   ────────────────────────────────────── */
const TwoCols = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: clamp(2rem, 5vw, 4rem);
  align-items: ${p => p.$align || 'start'};

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Divider = styled.div`
  width: 52px;
  height: 2px;
  background: ${S.safari};
  margin-bottom: clamp(1.25rem, 2.5vw, 1.75rem);
`;

/* ──────────────────────────────────────
   SECTION BREAKER
   ────────────────────────────────────── */
const SectionBreaker = styled.section`
  position: relative;
  width: 100%;
  height: clamp(580px, 60vh, 740px);
  overflow: hidden;
`;

const BreakerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 38%;
  display: block;
`;

const BreakerOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(to right, rgba(12, 44, 27, 0.78) 0%, rgba(12, 44, 27, 0.35) 28%, transparent 52%),
    linear-gradient(to bottom, transparent 50%, rgba(12, 44, 27, 0.45) 100%);

  @media (max-width: 768px) {
    background:
      linear-gradient(to bottom, transparent 35%, rgba(12, 44, 27, 0.82) 100%);
  }
`;

const BreakerQuoteWrap = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  padding: clamp(1.5rem, 5vw, 4rem);
  max-width: min(480px, 42vw);

  @media (max-width: 768px) {
    align-items: flex-end;
    justify-content: center;
    max-width: none;
    padding: 2rem 1.5rem 2.25rem;
  }
`;

const BreakerQuote = styled.p`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.5rem, 3.2vw, 2.5rem);
  font-style: italic;
  font-weight: 300;
  color: #fff;
  line-height: 1.35;
  text-shadow: 0 2px 28px rgba(0, 0, 0, 0.45);

  @media (max-width: 768px) {
    text-align: center;
    max-width: 22rem;
  }
`;

/* ──────────────────────────────────────
   ANIMALS GALLERY STRIP
   ────────────────────────────────────── */
const AnimalStrip = styled.div`
  overflow: hidden;
  border-radius: 14px;

  img {
    width: 100%;
    height: clamp(280px, 40vw, 420px);
    object-fit: cover;
    object-position: center 40%;
    display: block;
  }
`;

/* ──────────────────────────────────────
   FULL DAY OUT
   ────────────────────────────────────── */
const DayPillars = styled.ul`
  list-style: none;
  margin: 0 0 clamp(2.5rem, 5vw, 3.5rem);
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem 0.65rem;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

const DayPillar = styled.li`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  background: ${p => (p.$fill ? S.safari : 'rgba(12, 44, 27, 0.06)')};
  color: ${p => (p.$fill ? '#fff' : S.jungle)};
  border: 1px solid ${p => (p.$fill ? 'transparent' : 'rgba(12, 44, 27, 0.1)')};
`;

const DayBlock = styled.div`
  display: grid;
  grid-template-columns: ${p => (p.$reverse ? '0.95fr 1.05fr' : '1.05fr 0.95fr')};
  gap: clamp(1.5rem, 4vw, 3rem);
  align-items: center;
  margin-bottom: clamp(3rem, 6vw, 4.5rem);

  &:last-of-type {
    margin-bottom: 0;
  }

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const DayCopy = styled.div`
  order: ${p => (p.$reverse ? 2 : 1)};

  @media (max-width: 860px) {
    order: 2;
  }
`;

const DayMedia = styled.div`
  order: ${p => (p.$reverse ? 1 : 2)};
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 0.65rem;
  height: clamp(300px, 40vw, 460px);

  @media (max-width: 860px) {
    order: 1;
    height: clamp(260px, 55vw, 360px);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const DayPhoto = styled.button`
  position: relative;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  background: ${S.jungle};
  min-height: 0;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.5s ease;
  }

  &:hover img {
    transform: scale(1.04);
  }

  @media (max-width: 480px) {
    height: 220px;

    &:first-child {
      height: 260px;
    }
  }
`;

const DayBody = styled.p`
  margin: 0 0 1rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.92rem, 1.7vw, 1.02rem);
  line-height: 1.65;
  color: rgba(28, 28, 30, 0.62);
  max-width: 34rem;
`;

const DayLinkRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 0.5rem;
`;

const DayLink = styled.button`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.75rem 1.35rem;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;
  border: ${p => (p.$primary ? 'none' : `1.5px solid ${S.jungle}`)};
  background: ${p => (p.$primary ? S.safari : 'transparent')};
  color: ${p => (p.$primary ? '#fff' : S.jungle)};
  box-shadow: ${p => (p.$primary ? '0 4px 18px rgba(200, 134, 26, 0.35)' : 'none')};

  &:hover {
    transform: translateY(-1px);
    background: ${p => (p.$primary ? S.safari : S.jungle)};
    color: #fff;
  }
`;

/* ──────────────────────────────────────
   WITCH HANDOFF (Halloween tease)
   ────────────────────────────────────── */
const witchFade = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const WitchPanel = styled.section`
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #0d0a14 0%, #1a0a22 60%, #0a0d10 100%);
  padding: clamp(2.5rem, 7vw, 5rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
  animation: ${witchFade} 0.7s ease both;
`;

const WitchBg = styled.div`
  position: absolute;
  inset: 0;
  background-image: url('https://BelvoirCastlePull.b-cdn.net/Website/5S5A1084-2%20(1).webp');
  background-size: cover;
  background-position: center 30%;
  opacity: 0.14;
`;

const WitchInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 640px;
  margin: 0 auto;
`;

const WitchGlyph = styled.div`
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  margin: 0 0 0.75rem;
  line-height: 1;
  color: rgba(200, 160, 255, 0.75);
`;

const WitchHeadline = styled.h2`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.4rem, 3.5vw, 2rem);
  font-weight: 700;
  color: #e8d5ff;
  margin: 0 0 0.75rem;
  line-height: 1.25;
`;

const WitchBody = styled.p`
  font-size: clamp(0.88rem, 1.6vw, 1rem);
  color: rgba(232, 213, 255, 0.7);
  margin: 0 0 1.75rem;
  line-height: 1.65;
`;

const WitchBtn = styled.button`
  display: inline-block;
  padding: 0.85rem 2.25rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #4a1060 0%, #7b2fa0 100%);
  color: #e8d5ff;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  border: none;
  cursor: pointer;
  box-shadow: 0 6px 22px rgba(100, 30, 140, 0.4);
  transition: filter 0.2s ease, transform 0.2s ease;

  &:hover {
    filter: brightness(1.1);
    transform: translateY(-1px);
  }
`;

/* ──────────────────────────────────────
   COMPONENT
   ────────────────────────────────────── */
const SummerSafariEvent = () => {
  const navigate = useNavigate();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeImg, setActiveImg] = useState(null);
  const [shareFeedback, setShareFeedback] = useState('');

  const bookTickets = () => navigate('/belvoir-summer-safari/tickets');

  const sharePage = async () => {
    const url = getCanonicalUrl('belvoir-summer-safari');
    const shareData = {
      title: 'Belvoir Summer Safari — Summer 2026',
      text: 'Follow the clues. Find the animals. Meet them for real. Belvoir Summer Safari, 13 July – 1 September 2026.',
      url,
    };

    try {
      if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
        await navigator.share(shareData);
        return;
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
    }

    try {
      await navigator.clipboard.writeText(url);
      setShareFeedback('Link copied — share it with friends & family!');
    } catch {
      window.prompt('Copy this link to share:', url);
    }

    window.setTimeout(() => setShareFeedback(''), 3500);
  };

  const openLightbox = (src) => {
    setActiveImg(src);
    setGalleryOpen(true);
  };

  return (
    <>
      <Helmet>
        {/* ── Primary ── */}
        <title>Belvoir Summer Safari — Family Day Out | Belvoir Castle, Leicestershire</title>
        <meta
          name="description"
          content="Follow the clues. Find the animals. Meet them for real. Belvoir Summer Safari is an adventure playground trail for families at Belvoir Castle, Leicestershire. Animal Park included. 13 July – 1 September 2026."
        />
        <meta
          name="keywords"
          content="Belvoir Summer Safari, family day out Leicestershire, things to do with kids East Midlands, Belvoir Castle summer 2026, animal park Leicestershire, family trail Grantham, children's adventure trail, days out Nottinghamshire, days out Lincolnshire, things to do school holidays, summer holidays 2026, adventure trail for kids, Belvoir Castle tickets, family days out near me, Melton Mowbray days out"
        />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={getCanonicalUrl('belvoir-summer-safari')} />
        {/* AI-readable static version — full content without JavaScript */}
        <link rel="alternate" type="text/html" href="https://www.belvoircastle.com/belvoir-summer-safari.html" title="Belvoir Summer Safari — static readable version" />
        <link rel="help" href="https://www.belvoircastle.com/llms.txt" title="Belvoir Castle AI-readable content index" />

        {/* ── Open Graph ── */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:title" content="Belvoir Summer Safari — Family Day Out at Belvoir Castle" />
        <meta
          property="og:description"
          content="Follow the clues. Find the animals. Meet them for real. Adventure playground trail for families. Animal Park included with every ticket. 13 July – 1 September 2026."
        />
        <meta property="og:url" content={getCanonicalUrl('belvoir-summer-safari')} />
        <meta property="og:image" content="https://www.belvoircastle.com/wp/wp-content/uploads/2026/06/7.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Adventure playground tower at Belvoir Castle — Belvoir Summer Safari 2026" />
        <meta property="og:locale" content="en_GB" />

        {/* ── Twitter / X card ── */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@BelvoirCastle" />
        <meta name="twitter:title" content="Belvoir Summer Safari — Family Day Out at Belvoir Castle" />
        <meta
          name="twitter:description"
          content="Follow the clues. Find the animals. Meet them for real. Adventure playground trail — Animal Park included. 13 July – 1 September 2026."
        />
        <meta name="twitter:image" content="https://www.belvoircastle.com/wp/wp-content/uploads/2026/06/7.png" />
        <meta name="twitter:image:alt" content="Belvoir Summer Safari 2026 — family adventure trail at Belvoir Castle" />

        {/* ── Event JSON-LD rich result ── */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Event',
          name: 'Belvoir Summer Safari',
          description: 'An adventure playground trail for families at Belvoir Castle, Leicestershire. Follow clues around the playground, meet the animals in the Animal Park, and earn a free ice cream at the Farm Shop. Animal Park included with every Castle or Gardens ticket. Make it a full day with the Castle, gardens, and Retail Village.',
          image: ['https://www.belvoircastle.com/wp/wp-content/uploads/2026/06/7.png'],
          url: getCanonicalUrl('belvoir-summer-safari'),
          startDate: '2026-07-13',
          endDate: '2026-09-01',
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          location: {
            '@type': 'Place',
            name: 'Belvoir Castle',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Belvoir Castle',
              addressLocality: 'Grantham',
              postalCode: 'NG32 1PE',
              addressRegion: 'Leicestershire',
              addressCountry: 'GB'
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: 52.8893,
              longitude: -0.7814
            }
          },
          organizer: {
            '@type': 'Organization',
            name: 'Belvoir Castle',
            url: 'https://www.belvoircastle.com'
          },
          offers: {
            '@type': 'AggregateOffer',
            url: 'https://www.belvoircastle.com/belvoir-summer-safari/tickets',
            priceCurrency: 'GBP',
            availability: 'https://schema.org/InStock',
            description: 'Castle or Gardens entry — Animal Park included with every ticket. Group Saver for 2+ online tickets. HHA and RHS members pay £4.50 for Animal Park entry (addable when booking online).'
          },
          audience: {
            '@type': 'Audience',
            audienceType: 'Families with children'
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Is the Animal Park included in the ticket price?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. The Animal Park is included with both Castle entry and Gardens and Playground entry for standard ticket holders. HHA Members and RHS Members are required to pay £4.50 to enter the Animal Park; this can be added when booking online.'
              }
            },
            {
              '@type': 'Question',
              name: 'Do HHA or RHS members need to pay for the Animal Park?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. HHA Members and RHS Members are required to pay £4.50 to enter the Animal Park. This can be added when booking online.'
              }
            },
            {
              '@type': 'Question',
              name: 'When does Belvoir Summer Safari run?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Belvoir Summer Safari runs from 13 July to 1 September 2026. Belvoir Castle is open Monday to Sunday, 09:30 to 17:00.'
              }
            }
          ]
        })}</script>
      </Helmet>

      <Page>
        {/* ── HERO ── */}
        <HeroWrap>
          <HeroImageThenVideo image={HERO_COVER} videoSrc={playgroundVideo} />
          <HeroOverlay />
          <HeroPattern />
          <HeroContent>
            <HeroKicker>13 July – 1 September 2026 · Animal Park Included</HeroKicker>
            <HeroTitle>
              Belvoir <em>Summer Safari</em>
            </HeroTitle>
            <HeroStrapline>
              Follow the clues. Find the animals. Meet them for real.
            </HeroStrapline>
            <HeroBadgeRow>
              <Badge $fill>Group Saver — book together &amp; save</Badge>
              <Badge>Animal Park Included</Badge>
              <Badge>Free Ice Cream Reward</Badge>
              <Badge $hideMobile>Book early — avoid missing out</Badge>
            </HeroBadgeRow>
            <HeroButtons>
              <BtnPrimary onClick={bookTickets}>
                Book Tickets
              </BtnPrimary>
              <BtnGhost onClick={() => document.getElementById('trail-section').scrollIntoView({ behavior: 'smooth' })}>
                Discover the Trail
              </BtnGhost>
              <BtnGhost type="button" onClick={sharePage} $hideMobile>
                Share with Friends
              </BtnGhost>
              {shareFeedback ? <ShareFeedback>{shareFeedback}</ShareFeedback> : null}
            </HeroButtons>
          </HeroContent>
        </HeroWrap>

        {/* ── GROUP SAVER BANNER ── */}
        <OfferBanner>
          <OfferTag>Group Saver</OfferTag>
          <OfferText>
            Bring friends and family this summer — book <strong>2 or more tickets together online</strong> and save automatically. <strong>Book early</strong> to secure your dates and avoid missing out.
          </OfferText>
          <BtnPrimary
            style={{ padding: '0.65rem 1.5rem', fontSize: '0.75rem' }}
            onClick={bookTickets}
          >
            Book now
          </BtnPrimary>
        </OfferBanner>

        {/* ── WHAT IT IS ── */}
        <Section $bg={S.cream} $border>
          <Inner>
            <TwoCols $cols="1.1fr 0.9fr" $align="center">
              <div>
                <SectionKicker>Summer 2026</SectionKicker>
                <Divider />
                <SectionTitle>
                  The Adventure That Earns<br /><em>the Animals</em>
                </SectionTitle>
                <SectionIntro>
                  Belvoir Summer Safari is an adventure playground trail that takes explorers from Explorer HQ, through clue stops around the playground, all the way to the Animal Park — where the animals they've been reading about are waiting.
                </SectionIntro>
                <SectionIntro>
                  Come and go between the Animal Park, Retail Village, gardens and adventure playground all day long — a whole day of fun for the whole family.
                </SectionIntro>
                <SectionIntro style={{ marginBottom: 0 }}>
                  The Animal Park is included with every Castle and Gardens ticket. Follow all the clues and every child earns a free ice cream at the Farm Shop. Simple, physical, and completely memorable.
                </SectionIntro>
                <MidCta style={{ marginTop: '1.75rem' }}>
                  <MidCtaText>Ready to start the adventure?</MidCtaText>
                  <MidCtaBtn type="button" onClick={bookTickets}>Book Tickets</MidCtaBtn>
                </MidCta>
              </div>
              <AnimalStrip>
                <img src={safari1} alt="Animals at Belvoir" />
              </AnimalStrip>
            </TwoCols>
          </Inner>
        </Section>

        {/* ── SECTION BREAKER ── */}
        <SectionBreaker aria-hidden>
          <BreakerImg src={safari3} alt="" />
          <BreakerOverlay />
          <BreakerQuoteWrap>
            <BreakerQuote>
              &ldquo;The animals are the reward you earn by exploring.&rdquo;
            </BreakerQuote>
          </BreakerQuoteWrap>
        </SectionBreaker>

        {/* ── WHAT'S INCLUDED ── */}
        <Section $bg="#fff" $border>
          <Inner>
            <SectionKicker>Included with every ticket</SectionKicker>
            <Divider />
            <SectionTitle>Everything Your <em>Explorer Needs</em></SectionTitle>
            <FeatureGrid>
              {/* Trail Map */}
              <FeatureCard>
                <FeatureIcon>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={S.safari} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 7l6-3 6 3 6-3v13l-6 3-6-3-6 3V7z"/>
                    <line x1="9" y1="4" x2="9" y2="17"/>
                    <line x1="15" y1="7" x2="15" y2="20"/>
                    <circle cx="6" cy="11" r="1" fill={S.safari} stroke="none"/>
                    <circle cx="12" cy="10" r="1" fill={S.safari} stroke="none"/>
                    <circle cx="18" cy="12" r="1" fill={S.safari} stroke="none"/>
                  </svg>
                </FeatureIcon>
                <FeatureTitle>Safari Trail Map</FeatureTitle>
                <FeatureBody>Collect your trail map at Explorer HQ — the adventure playground. Eight stops, each revealing a new animal character. Complete the full trail to claim your reward.</FeatureBody>
              </FeatureCard>

              {/* Animal Park — paw print */}
              <FeatureCard>
                <FeatureIcon>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill={S.safari}>
                    <ellipse cx="6.5" cy="7.5" rx="2" ry="2.5"/>
                    <ellipse cx="11" cy="5.5" rx="2" ry="2.5"/>
                    <ellipse cx="15.5" cy="7" rx="2" ry="2.5"/>
                    <path d="M5 15c0-3.3 3.1-5.5 7-5.5s7 2.2 7 5.5c0 2.5-1.8 4.5-7 4.5S5 17.5 5 15z"/>
                  </svg>
                </FeatureIcon>
                <FeatureTitle>Animal Park Included</FeatureTitle>
                <FeatureBody>The Animal Park is included with both Castle and Gardens entry — no extra ticket, no separate booking. Meet the real animals you've been tracking on the trail.</FeatureBody>
              </FeatureCard>

              {/* Ice Cream — cone */}
              <FeatureCard>
                <FeatureIcon>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={S.safari} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 11h10l-5 10z"/>
                    <path d="M7 11a5 5 0 0 1 10 0"/>
                    <path d="M9.5 11a2.5 2.5 0 0 1 5 0"/>
                    <line x1="7" y1="14" x2="17" y2="14"/>
                  </svg>
                </FeatureIcon>
                <FeatureTitle>Free Ice Cream Reward</FeatureTitle>
                <FeatureBody>Every child who completes the full trail collects a voucher at the Animal Park. Walk to the Farm Shop to redeem: one free ice cream per explorer. Because every adventure needs a proper ending.</FeatureBody>
              </FeatureCard>

              {/* Animal Tales — open book */}
              <FeatureCard>
                <FeatureIcon>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={S.safari} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                    <line x1="6" y1="8" x2="9" y2="8"/>
                    <line x1="6" y1="11" x2="9" y2="11"/>
                    <line x1="15" y1="8" x2="18" y2="8"/>
                    <line x1="15" y1="11" x2="18" y2="11"/>
                  </svg>
                </FeatureIcon>
                <FeatureTitle>The Duchess's Animal Tales</FeatureTitle>
                <FeatureBody>Stories written by the Duchess of Rutland — one for each Animal Park character. Read them before you visit to meet the animals properly. Stuart the Calf and Katie the Goat are already live.</FeatureBody>
              </FeatureCard>

              {/* Castle / Gardens — castle battlements */}
              <FeatureCard>
                <FeatureIcon>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={S.safari} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="10" width="18" height="11" rx="1"/>
                    <path d="M3 10V7h3v3"/>
                    <path d="M10.5 10V7h3v3"/>
                    <path d="M18 10V7h3v3"/>
                    <path d="M9 21v-4a3 3 0 0 1 6 0v4"/>
                  </svg>
                </FeatureIcon>
                <FeatureTitle>Castle or Gardens Entry</FeatureTitle>
                <FeatureBody>Choose from a full Castle tour including the State Rooms, or a Gardens and Playground day out. Both include the trail, the Animal Park, and the ice cream reward.</FeatureBody>
              </FeatureCard>

              {/* Pony & Reptiles — ticket */}
              <FeatureCard $dark>
                <FeatureIcon style={{ background: 'rgba(200,134,26,0.18)' }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={S.safari} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 9a3 3 0 1 0 0 6V7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2a3 3 0 1 0 0-6z"/>
                    <line x1="9" y1="12" x2="15" y2="12" strokeDasharray="1.5 1.5"/>
                    <line x1="9" y1="9" x2="13" y2="9"/>
                    <line x1="9" y1="15" x2="13" y2="15"/>
                  </svg>
                </FeatureIcon>
                <FeatureTitle style={{ color: '#fff' }}>Pony Rides &amp; Reptiles</FeatureTitle>
                <FeatureBody $light>Pony rides and reptile encounters available as on-site upsells at the Animal Park on selected days. Add an extra layer of adventure for the explorers who want more.</FeatureBody>
              </FeatureCard>
            </FeatureGrid>
            <MidCta>
              <MidCtaText>Animal Park, trail map &amp; ice cream — all included with your ticket.</MidCtaText>
              <MidCtaBtn type="button" onClick={bookTickets}>Book Your Visit</MidCtaBtn>
            </MidCta>
          </Inner>
        </Section>

        {/* ── THE TRAIL ── */}
        <Section $bg={S.parchment} $border id="trail-section">
          <Inner>
            <SectionKicker>The Trail</SectionKicker>
            <Divider />
            <SectionTitle>
              Playground to Animal Park.<br /><em>Ice Cream at the End.</em>
            </SectionTitle>
            <SectionIntro>
              Eight clue stops around the adventure playground. Each one introduces an Animal Park resident — characters your children will recognise when they finally meet them for real.
            </SectionIntro>

            <TrailGrid>
              <TrailStep $bg={S.jungle}>
                <TrailNum>01</TrailNum>
                <TrailStepTitle>Explorer HQ</TrailStepTitle>
                <TrailStepBody>Adventure Playground. Collect your trail map. Your first clue starts here — and you can pick up bonus stamps around the playground too.</TrailStepBody>
              </TrailStep>
              <TrailStep $bg={S.forest}>
                <TrailNum>02–06</TrailNum>
                <TrailStepTitle>Adventure Playground Trail</TrailStepTitle>
                <TrailStepBody>Five clue stops around the adventure playground. Clue boards, animal facts and challenges. Follow the Summer Safari signs — and collect bonus stamps at the Retail Village along the way.</TrailStepBody>
              </TrailStep>
              <TrailStep $bg={S.terra}>
                <TrailNum>07</TrailNum>
                <TrailStepTitle>The Animal Park</TrailStepTitle>
                <TrailStepBody>"You followed the clues. Now meet them for real." Complete the trail, collect your voucher. Time to meet Gary, Stuart, Dave and friends.</TrailStepBody>
              </TrailStep>
              <TrailStep $bg={S.safari}>
                <TrailNum>08</TrailNum>
                <TrailStepTitle>Farm Shop Reward</TrailStepTitle>
                <TrailStepBody>Redeem your voucher at the Farm Shop counter. One free ice cream per explorer who completed the trail. Adventure earns its reward.</TrailStepBody>
              </TrailStep>
            </TrailGrid>

            <PullQuote>
              <p>&ldquo;You&apos;ve followed the clues. Now meet the animals for real.&rdquo;</p>
            </PullQuote>

            <MidCta>
              <MidCtaText>Planning a day out with friends or family? Group Saver applies when you book 2+ tickets online — book early to secure your spot.</MidCtaText>
              <MidCtaBtn type="button" onClick={bookTickets}>Book Tickets</MidCtaBtn>
            </MidCta>
          </Inner>
        </Section>

        {/* ── FULL DAY OUT ── */}
        <Section $bg="#fff" $border>
          <Inner>
            <SectionKicker>More Than a Trail</SectionKicker>
            <Divider />
            <SectionTitle>
              Make It a <em>Full Day Out</em>
            </SectionTitle>
            <SectionIntro>
              Summer Safari is the adventure that brings you here — but Belvoir is built for a whole day with the family. Explore the Castle, wander the gardens, stop for lunch in the Retail Village, and finish at the Animal Park.
            </SectionIntro>

            <DayPillars aria-label="What a full day at Belvoir includes">
              <DayPillar $fill>Safari Trail</DayPillar>
              <DayPillar>Castle &amp; State Rooms</DayPillar>
              <DayPillar>Formal Gardens</DayPillar>
              <DayPillar>Retail Village</DayPillar>
              <DayPillar>Animal Park</DayPillar>
              <DayPillar>Farm Shop Treat</DayPillar>
            </DayPillars>

            <DayBlock>
              <DayCopy>
                <SectionKicker>The Castle</SectionKicker>
                <Divider />
                <SectionTitle as="h3" style={{ fontSize: 'clamp(1.55rem, 3.5vw, 2.25rem)' }}>
                  Explore a Real <em>Stately Home</em>
                </SectionTitle>
                <DayBody>
                  Step inside Belvoir Castle and let the children look up — grand State Rooms, soaring ceilings, and centuries of stories. A Castle ticket turns the Safari into a full day of wonder indoors and out.
                </DayBody>
                <DayBody>
                  Outside, stroll the formal gardens on the way between playground clues and the Castle. It&apos;s the kind of day parents remember as much as the kids do.
                </DayBody>
                <DayLinkRow>
                  <DayLink $primary type="button" onClick={bookTickets}>
                    Book Castle Tickets
                  </DayLink>
                  <DayLink type="button" onClick={() => navigate('/about/castle-and-state-rooms')}>
                    Discover the Castle
                  </DayLink>
                </DayLinkRow>
              </DayCopy>
              <DayMedia>
                <DayPhoto type="button" onClick={() => openLightbox(IMG_CASTLE_GARDENS)} aria-label="View garden path to Belvoir Castle">
                  <img src={IMG_CASTLE_GARDENS} alt="Mother and child walking through Belvoir Castle gardens toward the Castle" />
                </DayPhoto>
                <DayPhoto type="button" onClick={() => openLightbox(IMG_CASTLE_WONDER)} aria-label="View children exploring the Castle">
                  <img src={IMG_CASTLE_WONDER} alt="Children looking up in wonder inside Belvoir Castle" />
                </DayPhoto>
              </DayMedia>
            </DayBlock>

            <DayBlock $reverse>
              <DayCopy $reverse>
                <SectionKicker>Retail Village</SectionKicker>
                <Divider />
                <SectionTitle as="h3" style={{ fontSize: 'clamp(1.55rem, 3.5vw, 2.25rem)' }}>
                  Pause, Eat &amp; <em>Explore</em>
                </SectionTitle>
                <DayBody>
                  Between trail stops and animal meetings, the Retail Village is your family basecamp — hearty lunches, coffee stops, boutique shops, and the Farm Shop where every completed trail earns a free ice cream.
                </DayBody>
                <DayBody>
                  Come and go through the day: playground to Village, Village to Animal Park, then back again. No rushing — just a proper summer day out.
                </DayBody>
                <DayLinkRow>
                  <DayLink $primary type="button" onClick={() => navigate('/retail-village')}>
                    Explore the Village
                  </DayLink>
                  <DayLink type="button" onClick={() => navigate('/retail-village/dining')}>
                    Where to Eat
                  </DayLink>
                </DayLinkRow>
              </DayCopy>
              <DayMedia $reverse>
                <DayPhoto type="button" onClick={() => openLightbox(IMG_RETAIL_DINING)} aria-label="View dining at the Retail Village">
                  <img src={IMG_RETAIL_DINING} alt="A meal being served at Belvoir Retail Village dining" />
                </DayPhoto>
                <DayPhoto type="button" onClick={() => openLightbox(IMG_TRAIL_BOOKLET)} aria-label="View the Safari Adventure Trail booklet">
                  <img src={IMG_TRAIL_BOOKLET} alt="Two boys reading the Belvoir Safari Adventure Trail booklet" />
                </DayPhoto>
              </DayMedia>
            </DayBlock>

            <MidCta style={{ marginTop: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
              <MidCtaText>Trail, Castle, gardens, Village and animals — one ticket, one brilliant day.</MidCtaText>
              <MidCtaBtn type="button" onClick={bookTickets}>Plan Your Full Day</MidCtaBtn>
            </MidCta>
          </Inner>
        </Section>

        {/* ── GALLERY ── */}
        <Section $bg={S.cream} $border>
          <Inner>
            <SectionKicker>The Experience</SectionKicker>
            <Divider />
            <SectionTitle>A Day They'll <em>Talk About</em></SectionTitle>
            <GalleryGrid>
              <GalleryItem
                $span2
                onClick={() => openLightbox(safari2)}
                style={{ cursor: 'pointer' }}
              >
                <img src={safari2} alt="Belvoir Summer Safari" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(safari4)} style={{ cursor: 'pointer' }}>
                <img src={safari4} alt="Belvoir animals" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(safari5)} style={{ cursor: 'pointer' }}>
                <img src={safari5} alt="Belvoir adventure playground trail" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(safari6)} style={{ cursor: 'pointer' }}>
                <img src={safari6} alt="Belvoir adventure playground" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(safariAnimals)} style={{ cursor: 'pointer' }}>
                <img src={safariAnimals} alt="Play animals" />
              </GalleryItem>
            </GalleryGrid>
            <MidCta>
              <MidCtaText>Give your explorers a summer day they'll remember.</MidCtaText>
              <MidCtaBtn type="button" onClick={bookTickets}>Book Now</MidCtaBtn>
            </MidCta>
          </Inner>
        </Section>

        {/* ── GROUP SAVER ── */}
        <Section $bg={S.jungle} $border={false}>
          <Inner>
            <SectionKicker $color={S.safari}>Group Saver</SectionKicker>
            <Divider />
            <SectionTitle $light>
              Bring More Explorers.<br /><em>Save More.</em>
            </SectionTitle>
            <SectionIntro $light>
              The more tickets you book together, the bigger the saving. Whether you're coming with grandparents, cousins, or a group of friends — Group Saver makes it easy to bring everyone along. Book early to secure your summer dates.
            </SectionIntro>

            <SaverGrid>
              {[
                { tickets: '2', tagline: 'Saver applies', label: 'tickets' },
                { tickets: '3', tagline: 'Save more', label: 'tickets' },
                { tickets: '4', tagline: 'Save more', label: 'tickets', active: true },
                { tickets: '5', tagline: 'Save more', label: 'tickets' },
                { tickets: '6+', tagline: 'Best saving', label: 'tickets', dark: true },
              ].map((tier) => (
                <SaverTier key={tier.tickets} $active={tier.active} $dark={tier.dark}>
                  <SaverTickets $light={tier.active || tier.dark}>{tier.tickets}</SaverTickets>
                  <SaverDiscount $light={tier.active || tier.dark}>{tier.tagline}</SaverDiscount>
                  <SaverLabel $light={tier.active || tier.dark}>{tier.label}</SaverLabel>
                </SaverTier>
              ))}
            </SaverGrid>

            <p style={{
              marginTop: '1.25rem',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: '0.78rem',
              color: 'rgba(255,255,255,0.4)',
              letterSpacing: '0.04em',
            }}>
              Group Saver applies to all online bookings of 2 or more tickets. Adult and child tickets combined — savings applied automatically at checkout.
            </p>

            <MidCta $dark style={{ marginTop: '2rem' }}>
              <MidCtaText $light>Bring the whole family — Group Saver when you book together online. Book early to avoid missing out.</MidCtaText>
              <MidCtaBtn type="button" onClick={bookTickets}>Book as a Group</MidCtaBtn>
            </MidCta>
          </Inner>
        </Section>

        {/* ── ESSENTIAL INFO ── */}
        <Section $bg="#fff" $border>
          <Inner>
            <SectionKicker>Plan Your Visit</SectionKicker>
            <Divider />
            <SectionTitle>Essential <em>Information</em></SectionTitle>
            <InfoGrid style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
              <InfoItem>
                <InfoLabel>Trail Period</InfoLabel>
                <InfoValue>13 July –<br />1 September 2026</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Opening Hours</InfoLabel>
                <InfoValue>Mon – Sun<br />09:30 – 17:00</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Animal Park</InfoLabel>
                <InfoValue>Included with<br />every ticket</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Ice Cream Reward</InfoLabel>
                <InfoValue>1 per child<br />who completes</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Location</InfoLabel>
                <InfoValue>Belvoir Castle<br />NG32 1PE</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Booking</InfoLabel>
                <InfoValue>Online only<br />for best price</InfoValue>
              </InfoItem>
            </InfoGrid>

            <TwoCols $cols="1fr 1fr">
              <div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '1.4rem', fontWeight: 700, color: S.jungle, margin: '0 0 0.75rem' }}>
                  Castle Entry
                </h3>
                <p style={{ fontFamily: 'system-ui, sans-serif', fontSize: '0.88rem', lineHeight: 1.65, color: 'rgba(28,28,30,0.62)', margin: '0 0 0.75rem' }}>
                  A full day in Belvoir Castle — explore the historic State Rooms, formal gardens, adventure playground, and the Animal Park. Trail map collected at the playground. Animal Park and ice cream reward included.
                </p>
              </div>
              <div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '1.4rem', fontWeight: 700, color: S.jungle, margin: '0 0 0.75rem' }}>
                  Gardens &amp; Playground Entry
                </h3>
                <p style={{ fontFamily: 'system-ui, sans-serif', fontSize: '0.88rem', lineHeight: 1.65, color: 'rgba(28,28,30,0.62)', margin: '0 0 0.75rem' }}>
                  The gardens, adventure playground, and Animal Park — all included. Collect your trail map at the playground and follow the Safari route to the animals. Ice cream reward included.
                </p>
              </div>
            </TwoCols>

            <p style={{
              margin: 'clamp(1.75rem, 3.5vw, 2.25rem) 0 0',
              padding: '1rem 1.15rem',
              borderRadius: 10,
              border: '1px solid rgba(200,134,26,0.28)',
              background: 'rgba(200,134,26,0.08)',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: '0.88rem',
              lineHeight: 1.55,
              color: 'rgba(28,28,30,0.72)',
            }}>
              <strong style={{ color: S.jungle }}>Please note:</strong> HHA Members and RHS Members are required to pay £4.50 to enter the Animal Park.
            </p>
          </Inner>
        </Section>

        {/* ── CTA ── */}
        <CtaSection>
          <CtaInner>
            <img
              src={PEACOCK}
              alt="Belvoir Castle"
              style={{ height: 52, width: 'auto', margin: '0 auto 1.5rem', display: 'block', opacity: 0.9 }}
            />
            <CtaTitle>
              Ready to Start<br /><em>the Adventure?</em>
            </CtaTitle>
            <CtaBody>
              Book your Belvoir Summer Safari tickets online with Group Saver — the more you book together, the more you save. Perfect for friends and family days out this summer. Book early to secure your dates and avoid missing out.
            </CtaBody>
            <CtaBtnRow>
              <CtaBtn type="button" onClick={bookTickets}>
                Book Tickets Now
              </CtaBtn>
              <CtaShareBtn type="button" onClick={sharePage}>
                Share with Friends
              </CtaShareBtn>
            </CtaBtnRow>
            {shareFeedback ? (
              <CtaMeta style={{ color: S.safari, marginTop: '0.85rem' }}>{shareFeedback}</CtaMeta>
            ) : (
              <CtaMeta>
                Trail open 13 July – 1 September 2026 · Animal Park included with every ticket · Group Saver for online group bookings
              </CtaMeta>
            )}
          </CtaInner>
        </CtaSection>

        <WitchPanel aria-label="Coming this October — Belvoir's Enchanted Night">
          <WitchBg aria-hidden="true" />
          <WitchInner>
            <WitchGlyph aria-hidden="true">
              <FontAwesomeIcon icon={faHatWizard} />
            </WitchGlyph>
            <WitchHeadline>The witch watched all summer.</WitchHeadline>
            <WitchBody>
              The safari ends. The curse begins. Come back in October as Curse Breakers — can your family
              solve the mystery and free Stuart's friends before the Enchanted Night is over?
            </WitchBody>
            <WitchBtn type="button" onClick={() => navigate('/halloween-belvoir-castle')}>
              Discover Enchanted Night
            </WitchBtn>
          </WitchInner>
        </WitchPanel>

        <Footer />
      </Page>

      {/* ── LIGHTBOX ── */}
      {galleryOpen && (
        <div
          onClick={() => setGalleryOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1.5rem', cursor: 'zoom-out',
          }}
        >
          <img
            src={activeImg}
            alt=""
            style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 }}
            onClick={e => e.stopPropagation()}
          />
          <button
            onClick={() => setGalleryOpen(false)}
            style={{
              position: 'absolute', top: '1.25rem', right: '1.25rem',
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 999, width: 40, height: 40, color: '#fff', fontSize: '1.1rem',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
};

export default SummerSafariEvent;
